/* Violet Eyes collection page: "Show more", sort that keeps filters, live count in the heading. */
(() => {
  if (window.veCollectionReady) return;
  window.veCollectionReady = true;

  /* Show more. The next batch loads by itself as the shopper nears the end of the grid (BNJMN, 28 Oct): three batches,
     then the button is shown and the rest is up to the shopper. The button stays in the page as the fallback when the
     browser cannot watch the scroll or a batch fails. The page remembers how many batches were loaded and where the
     shopper was, so Back from a product lands in the same place (see restoreGrid below). */
  const AUTO_BATCHES = 3;
  let loaded = 0; // batches added after the first one, on this view of the grid
  let autoLoaded = 0;
  let moreWatcher = null;
  let restoring = false;
  const onPage = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    return link ? new URL(link.getAttribute('href'), window.location.origin).pathname : '';
  };
  // Add the cards of a fetched page to the grid, skipping products already there (a category just picked puts its own first batch in front).
  const addCards = (grid, nextGrid) => {
    const have = new Set([...grid.children].map(onPage));
    [...nextGrid.children].forEach((card) => {
      if (have.has(onPage(card))) card.remove();
    });
    const firstNew = nextGrid.firstElementChild;
    grid.append(...nextGrid.children);
    return firstNew;
  };

  const stopAuto = () => {
    if (moreWatcher) moreWatcher.disconnect();
    moreWatcher = null;
    document.querySelector('.ve-more')?.classList.remove('ve-more--auto');
  };
  const watchMore = () => {
    if (moreWatcher) moreWatcher.disconnect();
    moreWatcher = null;
    const more = document.querySelector('.ve-more');
    const button = more && more.querySelector('[data-ve-show-more]');
    if (!button || !('IntersectionObserver' in window) || autoLoaded >= AUTO_BATCHES) {
      more?.classList.remove('ve-more--auto');
      return;
    }
    more.classList.add('ve-more--auto');
    moreWatcher = new IntersectionObserver((entries) => {
      if (restoring || !entries.some((entry) => entry.isIntersecting)) return;
      loadMore(button, true);
    }, { rootMargin: '0px 0px 100% 0px' });
    moreWatcher.observe(more);
  };

  const loadMore = async (button, auto) => {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(button.href);
      if (!response.ok) throw new Error(response.status);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = document.getElementById('product-grid');
      const nextGrid = html.getElementById('product-grid');
      if (!grid || !nextGrid || !button.isConnected) throw new Error('grid not found');

      const firstNew = addCards(grid, nextGrid);
      loaded += 1;
      if (auto) autoLoaded += 1;

      const nextButton = html.querySelector('[data-ve-show-more]');
      if (nextButton) {
        button.href = nextButton.href;
        button.removeAttribute('aria-busy');
        if (auto && autoLoaded < AUTO_BATCHES) watchMore(); // look again: the end of the grid may still be close
        else if (auto) stopAuto();
      } else {
        stopAuto();
        button.closest('.ve-more')?.remove();
      }

      // Move keyboard focus to the first new card's link (only when the shopper asked for the batch).
      const link = !auto && firstNew && firstNew.querySelector('a[href]');
      if (link) link.focus({ preventScroll: true });
    } catch (error) {
      if (auto) {
        // Do not leave the page by itself: show the button and let the shopper decide.
        button.removeAttribute('aria-busy');
        stopAuto();
      } else {
        window.location.href = button.href;
      }
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ve-show-more]');
    if (!button || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    loadMore(button, false);
  });

  /* Back from a product page: the batches that were loaded and the scroll position are kept for the visit (sessionStorage,
     one entry per address), then put back when the page is opened with Back / Forward. The cards are put back in the order
     they were shown in (a category picked last comes first, see ve-menu-picks.js). */
  const storeKey = () => `ve-grid:${window.location.pathname}${window.location.search}`;
  const saveGrid = () => {
    try {
      const grid = document.getElementById('product-grid');
      if (!grid || restoring) return;
      if (!loaded) {
        sessionStorage.removeItem(storeKey());
        return;
      }
      sessionStorage.setItem(storeKey(), JSON.stringify({ loaded, y: Math.round(window.scrollY), order: [...grid.children].map(onPage) }));
    } catch (error) { /* storage can be blocked: the page just starts at the top */ }
  };
  window.addEventListener('pagehide', saveGrid);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') saveGrid(); });

  const restoreGrid = async () => {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(storeKey()) || 'null'); } catch (error) { saved = null; }
    const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    const grid = document.getElementById('product-grid');
    if (!saved || !saved.loaded || !grid || !navigation || navigation.type !== 'back_forward') {
      try { window.history.scrollRestoration = 'auto'; } catch (error) { /* ignore */ }
      watchMore();
      return;
    }
    try { window.history.scrollRestoration = 'manual'; } catch (error) { /* ignore */ }
    restoring = true;
    const container = document.getElementById('ProductGridContainer');
    if (container) container.style.opacity = '0';
    let lastHtml = null;
    try {
      const pages = await Promise.all(
        Array.from({ length: saved.loaded }, (_, index) => {
          const url = new URL(window.location.href);
          url.searchParams.set('page', String(index + 2));
          return fetch(url).then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))));
        })
      );
      pages.forEach((text) => {
        lastHtml = new DOMParser().parseFromString(text, 'text/html');
        const nextGrid = lastHtml.getElementById('product-grid');
        if (nextGrid) addCards(grid, nextGrid);
      });
      if (Array.isArray(saved.order)) {
        const rank = new Map(saved.order.map((path, index) => [path, index]));
        const place = (card) => (rank.has(onPage(card)) ? rank.get(onPage(card)) : 1e9);
        grid.append(...[...grid.children].sort((a, b) => place(a) - place(b)));
      }
      loaded = saved.loaded;
      autoLoaded = saved.loaded;
      const more = document.querySelector('.ve-more');
      const nextButton = lastHtml && lastHtml.querySelector('[data-ve-show-more]');
      const button = more && more.querySelector('[data-ve-show-more]');
      if (button && nextButton) button.href = nextButton.href;
      else if (more) more.remove();
    } catch (error) {
      loaded = 0; // could not rebuild the batches: start at the top as before
      saved.y = 0;
    }
    window.scrollTo(0, saved.y || 0);
    requestAnimationFrame(() => {
      window.scrollTo(0, saved.y || 0);
      restoring = false;
      if (container) {
        container.style.transition = 'opacity 0.3s ease';
        container.style.opacity = '';
      }
      watchMore();
    });
  };
  // A page that would otherwise stay hidden (slow network) is shown after a few seconds.
  setTimeout(() => {
    const container = document.getElementById('ProductGridContainer');
    if (container && container.style.opacity === '0') container.style.opacity = '';
  }, 6000);

  // Sort: submit the filter panel's form so active filters are kept.
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-ve-sort]');
    if (!select) return;
    const form = document.getElementById(select.getAttribute('form'));
    if (form) form.dispatchEvent(new Event('input', { bubbles: true }));
  });

  // Keep "(n)" in the heading in step with the filtered product count.
  const syncCount = () => {
    const source = document.getElementById('ProductCount');
    const target = document.querySelector('[data-ve-count]');
    if (!source || !target) return;
    const match = source.textContent.replace(/[,.\s]/g, '').match(/\d+/);
    if (match) target.textContent = `(${match[0]})`;
  };
  let countObserver;
  const watch = () => {
    const source = document.getElementById('ProductCount');
    if (countObserver) countObserver.disconnect();
    if (source) {
      countObserver = new MutationObserver(syncCount);
      countObserver.observe(source, { childList: true, characterData: true, subtree: true });
    }
  };

  /* Filter in the right-hand column (computers, collection pages): "Filter +" opens the groups
     under it, each group opens with + / –, and a click on a choice loads the filtered products in
     place (no Apply button). The column and the products are re-rendered from the server, then the
     open groups and keyboard focus are put back. */
  const filterPanel = () => document.getElementById('VeFilterPanel');

  /* Hamburger + margins layout (BNJMN, 28 Oct): the Filter panel and its groups slide open and shut, the choices
     fading in one after the other (same movement as the menu's categories). Other layouts keep the plain toggle. */
  const veEase = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const slideFilter = () => !!document.querySelector('[data-ve-d6]') && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  const filterColumn = () => document.getElementById('main-collection-filters');
  const slideOpenClose = (el, expand, setState, collapsedHeight = 0, slow = false) => {
    const duration = expand ? 420 : slow ? 900 : 320;
    const column = filterColumn();
    /* The Filter column shrink-wraps its text and the L's left line sits on its edge, so its width glides with the
       height (otherwise the line jumps sideways when the panel or a group opens or shuts). */
    const widthFrom = column ? getComputedStyle(column).width : '';
    const was = getComputedStyle(el);
    /* The whole panel has 6px / 12px of padding that goes with its height; groups only animate their height.
       Heights are read as computed values, so they match the element's box-sizing (the panel is content-box). */
    const flat = collapsedHeight === 0;
    const pick = (style) => (flat ? { height: style.height, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom } : { height: style.height });
    let current = was.display !== 'none' ? pick(was) : null;
    if (current && !/px$/.test(current.height)) current = null;
    if (el.veAnim) el.veAnim.cancel();
    if (el.veFade) { el.veFade.cancel(); el.veFade = null; }
    if (column && column.veWidthAnim) column.veWidthAnim.cancel();
    setState(true);
    const open = pick(getComputedStyle(el));
    const shut = flat ? { height: '0px', paddingTop: '0px', paddingBottom: '0px' } : { height: `${collapsedHeight}px` };
    let widthTo = '';
    if (column) {
      if (expand) widthTo = getComputedStyle(column).width;
      else { setState(false); widthTo = getComputedStyle(column).width; setState(true); }
    }
    el.style.overflow = 'hidden';
    const anim = el.animate(expand ? [current || shut, open] : [current || open, shut], { duration, easing: veEase });
    el.veAnim = anim;
    if (column && widthFrom && widthTo && Math.abs(parseFloat(widthFrom) - parseFloat(widthTo)) > 0.5) {
      const widthAnim = column.animate([{ width: widthFrom }, { width: widthTo }], { duration, easing: veEase });
      column.veWidthAnim = widthAnim;
      widthAnim.onfinish = () => { if (column.veWidthAnim === widthAnim) column.veWidthAnim = null; };
    }
    if (expand) {
      [...el.children].forEach((item, index) => {
        if (item.matches('summary')) return;
        item.animate([{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], { duration: 360, delay: 60 + index * 35, easing: veEase, fill: 'backwards' });
      });
    }
    /* The self-closing Filter (below) also fades out while it shuts. */
    const fade = !expand && slow ? el.animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing: 'ease-in', fill: 'forwards' }) : null;
    el.veFade = fade;
    anim.onfinish = () => {
      el.veAnim = null;
      el.style.overflow = '';
      if (fade) { fade.cancel(); if (el.veFade === fade) el.veFade = null; }
      if (!expand) setState(false);
    };
  };

  const setFilterOpen = (open, animate = false, slow = false) => {
    const panel = filterPanel();
    const toggle = document.querySelector('[data-ve-filter-toggle]');
    if (!panel || !toggle) return;
    toggle.setAttribute('aria-expanded', String(open));
    if (animate && slideFilter() && panel.animate) {
      slideOpenClose(panel, open, (visible) => { panel.hidden = !visible; }, 0, slow);
      return;
    }
    panel.hidden = !open;
  };

  /* Hamburger + margins layout (BNJMN, 28 Oct): an open Filter with nothing ticked closes itself 3 seconds after it
     opened, fading out as it shuts. It waits while the mouse is over the Filter column or a key-focused item is in it,
     and counts 3 seconds again after the mouse leaves. A ticked filter (price or any filter of the panel; the picked
     menu categories do not count) keeps it open. Computers only. */
  const CATEGORY_PARAMS = ['filter.p.product_type', 'filter.p.tag'];
  const filtersApplied = () => [...new URLSearchParams(window.location.search).keys()].some((key) => key.startsWith('filter.') && !CATEGORY_PARAMS.includes(key));
  const autoCloseOn = () => !!document.querySelector('[data-ve-d6]') && matchMedia('(min-width: 990px)').matches;
  const filterToggle = () => document.querySelector('[data-ve-filter-toggle]');
  const filterBusy = () => {
    const column = filterColumn();
    if (!column) return false;
    const focused = document.activeElement;
    return column.matches(':hover') || (column.contains(focused) && focused.matches(':focus-visible'));
  };
  let filterTimer = 0;
  const armFilterTimer = () => {
    clearTimeout(filterTimer);
    const open = () => filterToggle()?.getAttribute('aria-expanded') === 'true';
    if (!autoCloseOn() || !open() || filtersApplied()) return;
    filterTimer = setTimeout(() => {
      if (!autoCloseOn() || !open() || filtersApplied() || filterBusy()) return;
      setFilterOpen(false, true, true);
    }, 3000);
  };
  const filterLeft = (event) => {
    const column = filterColumn();
    if (column && column.contains(event.target) && !(event.relatedTarget && column.contains(event.relatedTarget))) armFilterTimer();
  };
  document.addEventListener('mouseout', filterLeft);
  document.addEventListener('focusout', filterLeft);

  const setSortOpen = (open) => {
    const panel = document.getElementById('VeSortPanel');
    const toggle = document.querySelector('[data-ve-sort-toggle]');
    if (!panel || !toggle) return;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  };

  // Loading effect on computers (990px+): no white layer. The old cards fade out one after another in a wave (the first 9 cards in
  // order, the rest together with the last), then the new cards fade in the same way. Web Animations sit above Dawn's own per-card
  // slide-in, so the cards of a swapped grid get `data-ve-shown` (assets/ve-collection.css switches Dawn's animation off for them).
  // Phones and reduced motion keep the usual behaviour.
  const FADE_MS = 300;
  const FADE_STEP_MS = 25;
  const FADE_STEPS = 8;
  const FADE_TOTAL_MS = FADE_MS + FADE_STEPS * FADE_STEP_MS;
  const slideMotion = () => matchMedia('(min-width: 990px)').matches && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  // A category picked in the hamburger menu (29 Oct, BNJMN: faster): all cards fade together, 0.15 s, no waiting for the wave.
  const QUICK_MS = 150;
  const QUICK_OUT_MS = 400; // the fade out is slower, so the page is blank for less time while the new cards are fetched
  const fadeCards = (grid, from, to, fill, quick) => {
    grid.querySelectorAll('.grid__item').forEach((card, index) => {
      card.animate([{ opacity: from }, { opacity: to }], {
        duration: quick ? (to === 0 ? QUICK_OUT_MS : QUICK_MS) : FADE_MS,
        delay: quick ? 0 : Math.min(index, FADE_STEPS) * FADE_STEP_MS,
        easing: 'ease-in-out',
        fill
      });
    });
  };
  const fadeOutCards = (grid, quick) => {
    if (!slideMotion() || grid.dataset.veFading) return;
    grid.dataset.veFading = '1';
    fadeCards(grid, 1, 0, 'forwards', quick);
  };
  const fadeInCards = (grid, quick) => {
    delete grid.dataset.veFading;
    if (!slideMotion()) return;
    grid.querySelectorAll('.grid__item').forEach((card) => { card.dataset.veShown = ''; });
    fadeCards(grid, 0, 1, 'backwards', quick);
  };

  let filterRequest = 0;
  const loadFilters = async (url, push = true, keepSort = false, pruned = false) => {
    const wrapper = document.getElementById('main-collection-filters');
    const grid = document.getElementById('ProductGridContainer');
    if (!wrapper || !grid) {
      window.location.href = url;
      return;
    }
    const target = new URL(url, window.location.href);
    target.searchParams.delete('page');
    target.searchParams.delete('section_id');
    // Filter links were rendered with the sort of their time; use the sort chosen now.
    const sort = document.querySelector('[data-ve-sort]');
    if (!keepSort && sort && sort.value) target.searchParams.set('sort_by', sort.value);

    const request = ++filterRequest;
    const panelWasOpen = filterPanel() ? !filterPanel().hidden : false;
    const sortWasOpen = document.getElementById('VeSortPanel') ? !document.getElementById('VeSortPanel').hidden : false;
    const openGroups = [...wrapper.querySelectorAll('.ve-filter__group[open]')].map((group) => group.id);
    const active = document.activeElement;
    const focusSort = active && active.matches('[data-ve-sort-link]') ? active.dataset.veSortLink : null;
    const focusSortToggle = active && active.matches('[data-ve-sort-toggle]');
    const focusGroup = active && active.closest('.ve-filter__group');
    const focusLabel = active && active.matches('.ve-filter__value') ? active.firstChild.textContent.trim() : null;
    const focusIsToggle = active && active.matches('[data-ve-filter-toggle]');

    grid.querySelector('.collection')?.classList.add('loading');
    const quick = !!window.veQuickLoad;
    window.veQuickLoad = false;
    fadeOutCards(grid, quick);
    const loadStart = performance.now();
    try {
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', wrapper.dataset.id);
      // A picked category's own first batch goes in front (assets/ve-menu-picks.js): fetched together with the page, the grid stays
      // in its loading state until both are here, then it is shown once.
      const frontRequest = !window.veMenuPicks ? null : window.veMenuPicks.begin(target, wrapper.dataset.id);
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(response.status);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request !== filterRequest) return;
      const nextWrapper = html.getElementById('main-collection-filters');
      const nextGrid = html.getElementById('ProductGridContainer');
      if (!nextWrapper || !nextGrid) throw new Error('section not found');

      // A filter picked earlier that the new set of categories cannot have (a skirt Style left over after Skirts is taken
      // off, say) would leave the page empty with nothing to untick: drop those values, once, and load again.
      if (!pruned) {
        const dead = [...html.querySelectorAll('#VePhoneFilter .ve-pf__list input:checked')].filter(
          (input) => input.parentElement.querySelector('.ve-pf__count')?.textContent.trim() === '(0)'
        );
        if (dead.length) {
          const kept = [...target.searchParams].filter(([name, value]) => !dead.some((input) => input.name === name && input.value === value));
          [...new Set([...target.searchParams.keys()])].forEach((name) => target.searchParams.delete(name));
          kept.forEach(([name, value]) => target.searchParams.append(name, value));
          return loadFilters(target.toString(), push, true, true);
        }
      }

      if (frontRequest) {
        const front = await frontRequest;
        if (request !== filterRequest) return;
        window.veMenuPicks.apply(nextGrid, front);
      }

      // Computers: let the old cards finish fading out before the new ones replace them.
      const outWait = slideMotion() ? (quick ? QUICK_OUT_MS : FADE_TOTAL_MS) - (performance.now() - loadStart) : 0;
      if (outWait > 0) {
        await new Promise((resolve) => setTimeout(resolve, outWait));
        if (request !== filterRequest) return;
      }

      wrapper.innerHTML = nextWrapper.innerHTML;
      grid.innerHTML = nextGrid.innerHTML;
      fadeInCards(grid, quick);
      // The phone Filter panel sits outside the two blocks above: bring it up to date too (its groups and values depend on the categories).
      const phonePanel = document.getElementById('VePhoneFilter');
      const nextPhonePanel = html.getElementById('VePhoneFilter');
      if (phonePanel && nextPhonePanel && phonePanel.hidden) phonePanel.replaceWith(document.importNode(nextPhonePanel, true));
      setFilterOpen(panelWasOpen);
      setSortOpen(sortWasOpen);
      openGroups.forEach((id) => document.getElementById(id)?.setAttribute('open', ''));
      watch();
      // A new set of products: counting starts again and the end of the grid is watched again.
      loaded = 0;
      autoLoaded = 0;
      watchMore();

      // Same state shape as Dawn's facets.js, so its back / forward handler shows the same view.
      if (push) window.history.pushState({ searchParams: target.searchParams.toString() }, '', target.toString());
      armFilterTimer();

      // Put keyboard focus back where it was.
      if (focusSort) {
        document.querySelector(`[data-ve-sort-link="${CSS.escape(focusSort)}"]`)?.focus({ preventScroll: true });
      } else if (focusSortToggle) {
        document.querySelector('[data-ve-sort-toggle]')?.focus();
      } else if (focusIsToggle) {
        document.querySelector('[data-ve-filter-toggle]')?.focus();
      } else if (focusGroup) {
        const group = document.getElementById(focusGroup.id);
        const same = focusLabel
          ? [...(group?.querySelectorAll('.ve-filter__value') || [])].find((el) => el.firstChild.textContent.trim() === focusLabel)
          : null;
        (same || group?.querySelector('summary'))?.focus({ preventScroll: true });
      }
    } catch (error) {
      window.location.href = target.toString();
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-ve-filter-toggle]');
    if (toggle) {
      setFilterOpen(toggle.getAttribute('aria-expanded') !== 'true', true);
      armFilterTimer();
      return;
    }
    const groupSummary = event.target.closest('.ve-filter__summary');
    if (groupSummary && slideFilter()) {
      const group = groupSummary.parentElement;
      if (group.matches('details') && group.animate) {
        event.preventDefault();
        const expand = !group.open;
        slideOpenClose(group, expand, (visible) => { group.open = visible; }, groupSummary.getBoundingClientRect().height);
        return;
      }
    }
    const sortToggle = event.target.closest('[data-ve-sort-toggle]');
    if (sortToggle) {
      setSortOpen(sortToggle.getAttribute('aria-expanded') !== 'true');
      return;
    }
    const sortLink = event.target.closest('[data-ve-sort-link]');
    if (sortLink && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
      event.preventDefault();
      // Keep the chosen filters: same address, new sort (loadFilters reads the select).
      const select = document.querySelector('[data-ve-sort]');
      if (select) select.value = sortLink.dataset.veSortLink;
      const target = new URL(window.location.href);
      target.searchParams.set('sort_by', sortLink.dataset.veSortLink);
      loadFilters(target.toString());
      return;
    }
    const link = event.target.closest('[data-ve-filter-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    loadFilters(link.href);
  });

  // Price: from / to, applied when a field changes or Enter is pressed.
  const applyPrice = (form) => {
    const target = new URL(window.location.href);
    form.querySelectorAll('input[name]').forEach((input) => {
      if (input.value === '') target.searchParams.delete(input.name);
      else target.searchParams.set(input.name, input.value);
    });
    loadFilters(target.toString());
  };
  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-ve-price]');
    if (form) applyPrice(form);
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-ve-price]');
    if (!form) return;
    event.preventDefault();
    applyPrice(form);
  });

  // Used by assets/ve-menu-picks.js (menu categories): same in-place reload and history entry as a filter click.
  window.veCollection = { load: loadFilters };

  // Back / forward after filtering: show the filters of that address.
  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-ve-filter]')) loadFilters(window.location.href, false, true);
  });
  // A fresh page (a menu link, Shop all, the hamburger menu, a link from another page): the first cards fade in one after another too,
  // instead of Dawn's slide-in (29 Oct, BNJMN: "animate the products uploads from the menu and shop all").
  const enterGrid = () => {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.closest('.ve-main--collection') || !slideMotion()) return;
    fadeInCards(grid);
  };
  const start = () => { enterGrid(); watch(); restoreGrid(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
